import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import { getLupaStatus, getLupaColor } from "hhn-commons";

import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { DatesSetArg, EventClickArg } from "@fullcalendar/core";
import { IGroupInfo, IScheduledVisits } from "@home-health-notify/domain";
import dayjs from "dayjs";
import theme from "../theme";
import styled from "@emotion/styled";

export const MyCalendarWrapper = styled(Box)`
  height: 100%;
  flex-grow: 1;
`;

interface IMyCalendarProps {
  onDateRangeChange: (arg: DatesSetArg) => void;
  events: IScheduledVisits[];
  onEventClick: (eventData: EventClickArg) => void;
  showMapForEvents?: boolean;
  openCalendarMap?: (routeData: IScheduledVisits[]) => void;
  info?: IGroupInfo;
}

export const MyCalendar = ({
  onDateRangeChange,
  events,
  onEventClick,
  showMapForEvents = false,
  openCalendarMap,
  info,
}: IMyCalendarProps) => {
  const [dateRange, setDateRange] = React.useState<DatesSetArg>();

  const patientCareTimeline = info && info.patientCareTime;
  const lupaPeriodStart = patientCareTimeline?.startDate;
  const lupaPeriodEnd = patientCareTimeline?.endDate;
  const { actualVisitCount, lupa } = info || {};
  const lupaStatus = useMemo(
    () => lupa && actualVisitCount && getLupaStatus(lupa, actualVisitCount),
    [lupa, actualVisitCount],
  );

  const handleDateRangeChange = (arg: DatesSetArg) => {
    onDateRangeChange(arg);
    setDateRange(arg);
  };

  const mappedEvents = useMemo(
    () =>
      events.map(event => ({
        id: event.id,
        title: event.title,
        start: event.schedulerTime
          ? dayjs(event.schedulerTime).toDate()
          : dayjs(event.schedulerDate).startOf("day").toDate(),
        end: event.schedulerEndTime
          ? dayjs(event.schedulerEndTime).toDate()
          : dayjs(event.schedulerDate).endOf("day").toDate(),
      })),
    [events],
  );

  useEffect(() => {
    if (!dateRange) return;
    const dayCells = document.getElementsByClassName(
      "fc-daygrid-day-frame",
    ) as any;
    for (let i = 0; i < dayCells.length; i++) {
      const currentDay = dayjs(dateRange.start.getTime()).add(i, "day");
      const dayStart = currentDay.startOf("day").valueOf();
      const dayEnd = currentDay.endOf("day").valueOf();
      const cellFrame = dayCells[i] as any;
      const currentDayEvents = events.filter(
        (event: any) =>
          (event.schedulerTime >= dayStart && event.schedulerTime <= dayEnd) ||
          (event.schedulerEndTime >= dayStart &&
            event.schedulerEndTime <= dayEnd),
      );

      const lupaColor = getLupaColor(
        currentDay,
        lupaPeriodStart,
        lupaPeriodEnd,
        lupaStatus,
      );
      if (lupaColor) {
        cellFrame.style.backgroundColor = lupaColor;
      }

      if (currentDayEvents.length > 0) {
        cellFrame.style = `position: relative; padding-bottom: 16px; ${lupaColor ? `background-color: ${lupaColor};` : ""}`;
        const mapButton = document.createElement("div") as any;
        mapButton.className = "map-button";
        mapButton.style =
          "bottom: 0; right: 8px; position: absolute; cursor: pointer;";
        mapButton.style.display = showMapForEvents ? "block" : "none";
        mapButton.innerHTML = `
        <div class="map-button-circle" style="color: lightgray;">
          <span class="material-icons">map</span>
        </div>
      `;
        const buttonCircle = mapButton.querySelector(
          ".map-button-circle",
        ) as HTMLElement;
        cellFrame.addEventListener("mouseenter", () => {
          buttonCircle.style.color = theme.palette.primary.main;
        });
        cellFrame.addEventListener("mouseleave", () => {
          buttonCircle.style.color = "lightgray";
        });
        mapButton.addEventListener("click", (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          openCalendarMap && openCalendarMap(currentDayEvents);
        });
        cellFrame.appendChild(mapButton);
      } else {
        const mapButton = cellFrame.getElementsByClassName("map-button")[0];
        if (mapButton) {
          cellFrame.removeChild(mapButton);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, dateRange, showMapForEvents, openCalendarMap]);

  return (
    <MyCalendarWrapper>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
        }}
        initialView="dayGridMonth"
        weekends={true}
        height={"100%"}
        events={mappedEvents}
        datesSet={handleDateRangeChange}
        displayEventTime={false}
        eventDisplay={"block"}
        eventClick={eventData => onEventClick(eventData)}
      />
    </MyCalendarWrapper>
  );
};
